import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSelector } from "react-redux";
import merchant_services from "services/merchant_services";
import { networkTypeSelector } from "store/userSliceHelpers";
import { apiErrorMessage } from "utils/errors/parseApiError";

export interface UseBlockMerchantReturn {
  blockingMerchant: boolean;
  blockingMerchantMsg: string;
  blockMerchantError: boolean;
  blockMerchantErrorMsg: string;
  blockMerchantSuccess: boolean;
  blockMerchantSuccessMsg: string;
  handleBlockMerchant: (merchantId: string) => Promise<void>;
  handleRetry: () => Promise<void>;
  handleCloseError: () => void;
  handleCloseSuccess: () => void;
}

export function useBlockMerchant(): UseBlockMerchantReturn {
  const queryClient = useQueryClient();
  const networkType = useSelector(networkTypeSelector);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const [lastMerchantId, setLastMerchantId] = useState("");

  const openError = (msg: string) => {
    setLoadingMsg("");
    setIsLoading(false);
    setErrorMsg(msg);
    setShowError(true);
  };

  const handleCloseError = () => {
    setShowError(false);
    setErrorMsg("");
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    setSuccessMsg("");
  };

  const handleBlockMerchant = async (merchantId: string) => {
    setLastMerchantId(merchantId);

    try {
      setLoadingMsg("Blocking merchant…");
      setIsLoading(true);

      const result = await merchant_services.blockMerchant(
        merchantId,
        networkType,
      );
      console.log("useBlockMerchant => handleBlockMerchant result:", result);
      queryClient.invalidateQueries({ queryKey: ["merchants"] });
      queryClient.invalidateQueries({ queryKey: ["balances", "merchants"] });
      queryClient.invalidateQueries({ queryKey: ["merchant-lookup"] });

      setIsLoading(false);
      setSuccessMsg("Merchant has been blocked successfully.");
      setShowSuccess(true);
    } catch (error: any) {
      console.log("useBlockMerchant => handleBlockMerchant error:", error);
      openError(
        apiErrorMessage(error, "Failed to block merchant. Please try again."),
      );
    }
  };

  // ── Retry: re-runs the block with the last attempted merchant ID ──
  const handleRetry = () => handleBlockMerchant(lastMerchantId);

  return {
    blockingMerchant: isLoading,
    blockingMerchantMsg: loadingMsg,
    blockMerchantError: showError,
    blockMerchantErrorMsg: errorMsg,
    blockMerchantSuccess: showSuccess,
    blockMerchantSuccessMsg: successMsg,
    handleBlockMerchant,
    handleRetry,
    handleCloseError,
    handleCloseSuccess,
  };
}
